import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MessageSquare, RefreshCw, AlertTriangle, Check, X, MinusCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABELS = {
  "order-confirmed": "Order confirmed",
  "order-preparing": "Order preparing",
  "order-ready": "Order ready",
  "review-ask": "Review ask",
  "birthday-treat": "Birthday treat",
  "loyalty-reminder": "Loyalty reminder",
  "loyalty-blast": "Loyalty blast",
  "win-back": "Win-back",
  "reward": "Reward",
  "reservation-received": "Reservation received",
  "reservation-decision": "Reservation decision",
  "reservation-reminder": "Reservation reminder",
  "admin-alert": "Admin alert",
  "driver-dispatch": "Driver dispatch",
  general: "General",
};

function StatusChip({ status }) {
  const map = {
    sent: { icon: Check, cls: "border-green-600/60 text-green-500", label: "Sent" },
    failed: { icon: X, cls: "border-red-500/60 text-red-400", label: "Failed" },
    skipped: { icon: MinusCircle, cls: "border-[var(--mm-line)] text-[var(--mm-cream-muted)]", label: "Skipped" },
  };
  const m = map[status] || map.skipped;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[0.6rem] tracking-[0.16em] uppercase px-2 py-1 border ${m.cls}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
}

export default function SmsLogPanel({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sms-log?limit=200`, { headers: authHeaders });
      setItems(data.items || []);
    } catch { /* keep whatever we have */ } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const kinds = ["all", ...Array.from(new Set(items.map((i) => i.kind || "general")))];
  const visible = kind === "all" ? items : items.filter((i) => (i.kind || "general") === kind);
  const trialBlocked = items.some((i) => (i.error || "").includes("21608"));

  return (
    <div data-testid="sms-log-panel">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="section-label flex items-center gap-2">
          <MessageSquare size={14} /> SMS Activity
        </div>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="sms-log-kind-filter"
          className="bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] text-[var(--mm-cream)] text-xs px-3 py-2 outline-none"
        >
          {kinds.map((k) => (
            <option key={k} value={k}>{k === "all" ? "All types" : KIND_LABELS[k] || k}</option>
          ))}
        </select>
        <button onClick={load} data-testid="sms-log-refresh" className="btn-outline inline-flex items-center gap-2 !px-4 !py-2 text-[0.62rem]">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {trialBlocked && (
        <div className="flex items-start gap-2 border border-amber-500/50 text-amber-400 text-xs p-3 mb-5 leading-relaxed">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            Some texts are failing with Twilio error 21608 — your Twilio account is a TRIAL and can only text
            verified numbers. Upgrade at twilio.com (Billing) and everything below starts delivering automatically.
          </span>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="text-sm text-[var(--mm-cream-muted)]">{loading ? "Loading…" : "No texts logged yet."}</p>
      ) : (
        <div className="space-y-3">
          {visible.map((row) => (
            <div key={row.id} className="border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] p-4">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <StatusChip status={row.status} />
                <span className="text-[0.62rem] tracking-[0.18em] uppercase text-[var(--mm-accent)]">
                  {KIND_LABELS[row.kind] || row.kind || "General"}
                </span>
                <span className="text-xs text-[var(--mm-cream)]">{row.to}</span>
                <span className="text-xs text-[var(--mm-cream-muted)] ml-auto">
                  {row.created_at ? new Date(row.created_at).toLocaleString("en-AU") : ""}
                </span>
              </div>
              <p className="text-xs text-[var(--mm-cream-muted)] leading-relaxed">{row.body}</p>
              {row.error && <p className="text-[0.68rem] text-red-400 mt-1.5">{row.error}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
